/**
 * WII Benchmarking Dashboard
 * 
 * Global Workforce Observatory dashboard showing aggregate WII data
 * across users with metrics, force breakdown, distribution, and insights.
 * 
 * Location: /observatories/workforce-dashboard
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart3, Users, TrendingUp, TrendingDown, AlertTriangle,
  Building, PieChart, ArrowUpRight, ArrowDownRight, Globe,
  Gauge, Brain, Target, Shield, Zap, CheckCircle, Info,
  ArrowRight, Lightbulb, Activity
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useWorkforceIntelligence } from "@/contexts/WorkforceIntelligenceContext";
import { SEVEN_FORCES_ARRAY, WII_LEVELS } from "@/data/workforceIntelligence";
import BackNavigation from "@/components/BackNavigation";
import {
  GLOBAL_BENCHMARK_DATA,
  generateBenchmarkInsights,
  compareToGlobalBenchmark,
  compareForceScoresToBenchmark
} from "@/data/wiiBenchmarkData";

export default function WIIBenchmarkingDashboard() {
  const { hasCompletedAssessment, wiiScore, forceScores } = useWorkforceIntelligence();

  // Generate insights from benchmark data
  const insights = useMemo(() => generateBenchmarkInsights(), []);

  // Compare user to benchmark (if assessment completed)
  const userComparison = useMemo(() => {
    if (!hasCompletedAssessment || wiiScore === null) return null;
    return compareToGlobalBenchmark(wiiScore);
  }, [hasCompletedAssessment, wiiScore]);

  const forceComparison = useMemo(() => {
    if (!hasCompletedAssessment || !forceScores) return null;
    return compareForceScoresToBenchmark(forceScores);
  }, [hasCompletedAssessment, forceScores]);

  // Get sorted forces by average
  const sortedForces = useMemo(() => {
    return SEVEN_FORCES_ARRAY.map(force => ({
      ...force,
      average: GLOBAL_BENCHMARK_DATA.forceAverages[force.id]
    })).sort((a, b) => b.average - a.average);
  }, []);

  // Get insight icon
  const getInsightIcon = (iconName) => {
    switch (iconName) {
      case 'trending-up': return TrendingUp;
      case 'alert-triangle': return AlertTriangle;
      case 'pie-chart': return PieChart;
      case 'arrow-up-right': return ArrowUpRight;
      case 'arrow-down-right': return ArrowDownRight;
      case 'building': return Building;
      default: return Lightbulb;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="wii-benchmark-dashboard">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation />
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/observatories" className="text-sm text-white/60 hover:text-white">
              Observatories
            </Link>
            <span className="text-white/40">/</span>
            <Link to="/observatories/global" className="text-sm text-white/60 hover:text-white">
              Global Workforce Observatory
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-sm text-white font-medium">WII Dashboard</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <BarChart3 className="w-8 h-8" />
              <h1 className="font-heading text-3xl sm:text-4xl font-bold">
                WII Benchmarking Dashboard
              </h1>
            </div>
            <p className="text-lg text-white/80 max-w-2xl mb-2">
              Global Workforce Intelligence Index analytics and benchmarking data
            </p>
            <p className="text-sm text-white/60">
              Last updated: {new Date(GLOBAL_BENCHMARK_DATA.lastUpdated).toLocaleDateString()} • 
              Sample size: {GLOBAL_BENCHMARK_DATA.sampleSize.toLocaleString()} assessments
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Core Metrics Section */}
        <section className="mb-12" data-testid="core-metrics">
          <h2 className="font-heading text-2xl font-bold mb-6" style={{ color: '#991B1B' }}>
            Global WII Metrics
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Average WII */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Card className="border-[#E5E7EB] h-full" data-testid="metric-average-wii">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center">
                      <Gauge className="w-6 h-6 text-[#1E3A8A]" />
                    </div>
                    <Badge className="bg-[#1E3A8A]/10 text-[#1E3A8A]">Global</Badge>
                  </div>
                  <p className="text-sm text-[#1E3A8A]/60 mb-1">Average WII Score</p>
                  <p className="text-4xl font-bold text-[#1E3A8A]">
                    {GLOBAL_BENCHMARK_DATA.overallStats.averageWII}
                  </p>
                  <p className="text-xs text-[#1E3A8A]/50 mt-2">
                    Median: {GLOBAL_BENCHMARK_DATA.overallStats.medianWII} | 
                    Range: {GLOBAL_BENCHMARK_DATA.overallStats.minWII}-{GLOBAL_BENCHMARK_DATA.overallStats.maxWII}
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Total Users */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <Card className="border-[#E5E7EB] h-full" data-testid="metric-total-users">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-600" />
                    </div>
                    <Badge className="bg-green-100 text-green-700">Active</Badge>
                  </div>
                  <p className="text-sm text-[#1E3A8A]/60 mb-1">Total Assessments</p>
                  <p className="text-4xl font-bold text-[#1E3A8A]">
                    {GLOBAL_BENCHMARK_DATA.sampleSize.toLocaleString()}
                  </p>
                  <p className="text-xs text-[#1E3A8A]/50 mt-2">
                    +{GLOBAL_BENCHMARK_DATA.monthlyTrends[GLOBAL_BENCHMARK_DATA.monthlyTrends.length - 1].submissions} this month
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* High Readiness */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Card className="border-[#E5E7EB] h-full" data-testid="metric-high-readiness">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#4CAF50]/10 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-[#4CAF50]" />
                    </div>
                    <Badge className="bg-[#4CAF50]/10 text-[#4CAF50]">WII 71+</Badge>
                  </div>
                  <p className="text-sm text-[#1E3A8A]/60 mb-1">High Readiness</p>
                  <p className="text-4xl font-bold text-[#4CAF50]">
                    {GLOBAL_BENCHMARK_DATA.levelDistribution.high.percentage}%
                  </p>
                  <p className="text-xs text-[#1E3A8A]/50 mt-2">
                    {GLOBAL_BENCHMARK_DATA.levelDistribution.high.count.toLocaleString()} users at advanced level
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            {/* Trend */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              <Card className="border-[#E5E7EB] h-full" data-testid="metric-trend">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Activity className="w-6 h-6 text-blue-600" />
                    </div>
                    <Badge className="bg-blue-100 text-blue-700">Trend</Badge>
                  </div>
                  <p className="text-sm text-[#1E3A8A]/60 mb-1">3-Month Change</p>
                  <div className="flex items-center gap-2">
                    <p className="text-4xl font-bold text-green-600">+2.1</p>
                    <ArrowUpRight className="w-6 h-6 text-green-600" />
                  </div>
                  <p className="text-xs text-[#1E3A8A]/50 mt-2">
                    WII improving across all regions
                  </p>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* User Comparison (if assessment completed) */}
        {userComparison && (
          <section className="mb-12">
            <Card className="border-2 border-[#991B1B]/20 bg-[#991B1B]/5" data-testid="user-comparison">
              <CardContent className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Brain className="w-5 h-5 text-[#991B1B]" />
                  <h3 className="font-heading text-lg font-bold text-[#991B1B]">Your Benchmark Position</h3>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <p className="text-sm text-[#1E3A8A]/60 mb-1">Your WII</p>
                    <p className="text-3xl font-bold text-[#991B1B]">{userComparison.userWII}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-[#1E3A8A]/60 mb-1">vs Global Average</p>
                    <p className={`text-3xl font-bold ${userComparison.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {userComparison.difference >= 0 ? '+' : ''}{userComparison.difference.toFixed(1)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-[#1E3A8A]/60 mb-1">Percentile</p>
                    <p className="text-3xl font-bold text-[#1E3A8A]">{userComparison.percentile}th</p>
                  </div>
                </div>
                <p className="text-center text-sm text-[#1E3A8A]/70 mt-4">
                  {userComparison.description}
                </p>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Distribution Section */}
        <section className="mb-12" data-testid="distribution-section">
          <h2 className="font-heading text-2xl font-bold mb-6" style={{ color: '#991B1B' }}>
            Workforce Readiness Distribution
          </h2>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-3 gap-8">
                {/* Low Readiness */}
                <div className="text-center">
                  <div 
                    className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: `${WII_LEVELS.LOW.color}20` }}
                  >
                    <span className="text-2xl font-bold" style={{ color: WII_LEVELS.LOW.color }}>
                      {GLOBAL_BENCHMARK_DATA.levelDistribution.low.percentage}%
                    </span>
                  </div>
                  <h4 className="font-heading font-bold mb-1" style={{ color: WII_LEVELS.LOW.color }}>
                    Low Readiness
                  </h4>
                  <p className="text-xs text-[#1E3A8A]/60">WII 0-40</p>
                  <p className="text-sm text-[#1E3A8A]/70 mt-2">
                    {GLOBAL_BENCHMARK_DATA.levelDistribution.low.count.toLocaleString()} users
                  </p>
                  <Progress 
                    value={GLOBAL_BENCHMARK_DATA.levelDistribution.low.percentage} 
                    className="h-2 mt-3"
                  />
                </div>

                {/* Moderate Readiness */}
                <div className="text-center">
                  <div 
                    className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: `${WII_LEVELS.MODERATE.color}20` }}
                  >
                    <span className="text-2xl font-bold" style={{ color: WII_LEVELS.MODERATE.color }}>
                      {GLOBAL_BENCHMARK_DATA.levelDistribution.moderate.percentage}%
                    </span>
                  </div>
                  <h4 className="font-heading font-bold mb-1" style={{ color: WII_LEVELS.MODERATE.color }}>
                    Moderate Readiness
                  </h4>
                  <p className="text-xs text-[#1E3A8A]/60">WII 41-70</p>
                  <p className="text-sm text-[#1E3A8A]/70 mt-2">
                    {GLOBAL_BENCHMARK_DATA.levelDistribution.moderate.count.toLocaleString()} users
                  </p>
                  <Progress 
                    value={GLOBAL_BENCHMARK_DATA.levelDistribution.moderate.percentage} 
                    className="h-2 mt-3"
                  />
                </div>

                {/* High Readiness */}
                <div className="text-center">
                  <div 
                    className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ backgroundColor: `${WII_LEVELS.HIGH.color}20` }}
                  >
                    <span className="text-2xl font-bold" style={{ color: WII_LEVELS.HIGH.color }}>
                      {GLOBAL_BENCHMARK_DATA.levelDistribution.high.percentage}%
                    </span>
                  </div>
                  <h4 className="font-heading font-bold mb-1" style={{ color: WII_LEVELS.HIGH.color }}>
                    High Readiness
                  </h4>
                  <p className="text-xs text-[#1E3A8A]/60">WII 71-100</p>
                  <p className="text-sm text-[#1E3A8A]/70 mt-2">
                    {GLOBAL_BENCHMARK_DATA.levelDistribution.high.count.toLocaleString()} users
                  </p>
                  <Progress 
                    value={GLOBAL_BENCHMARK_DATA.levelDistribution.high.percentage} 
                    className="h-2 mt-3"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Seven Forces Breakdown */}
        <section className="mb-12" data-testid="force-breakdown">
          <h2 className="font-heading text-2xl font-bold mb-6" style={{ color: '#991B1B' }}>
            Seven Forces Breakdown
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {sortedForces.map((force, index) => {
              const userScore = forceComparison?.find(f => f.forceId === force.id);
              return (
                <motion.div
                  key={force.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Card className="border-[#E5E7EB] hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: force.color }}
                          />
                          <h4 className="font-heading font-semibold text-[#1E3A8A]">
                            {force.name}
                          </h4>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold" style={{ color: force.color }}>
                            {force.average.toFixed(1)}
                          </span>
                          {userScore && userScore.userScore !== null && (
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              userScore.difference > 0 ? 'bg-green-100 text-green-700' :
                              userScore.difference < 0 ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              You: {userScore.userScore}
                            </span>
                          )}
                        </div>
                      </div>
                      <Progress 
                        value={force.average} 
                        className="h-3 mb-2"
                      />
                      <p className="text-xs text-[#1E3A8A]/60 line-clamp-1">
                        {force.description.split('.')[0]}.
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Insights Section */}
        <section className="mb-12" data-testid="insights-section">
          <h2 className="font-heading text-2xl font-bold mb-6" style={{ color: '#991B1B' }}>
            Key Insights
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {insights.map((insight, index) => {
              const Icon = getInsightIcon(insight.icon);
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <Card className={`h-full border-l-4 ${
                    insight.type === 'strength' ? 'border-l-green-500' :
                    insight.type === 'gap' ? 'border-l-orange-500' :
                    insight.type === 'positive' ? 'border-l-blue-500' :
                    'border-l-[#1E3A8A]'
                  }`}>
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          insight.type === 'strength' ? 'bg-green-100' :
                          insight.type === 'gap' ? 'bg-orange-100' :
                          insight.type === 'positive' ? 'bg-blue-100' :
                          'bg-[#1E3A8A]/10'
                        }`}>
                          <Icon className={`w-5 h-5 ${
                            insight.type === 'strength' ? 'text-green-600' :
                            insight.type === 'gap' ? 'text-orange-600' :
                            insight.type === 'positive' ? 'text-blue-600' :
                            'text-[#1E3A8A]'
                          }`} />
                        </div>
                        <div>
                          <h4 className="font-heading font-bold text-[#1E3A8A] text-sm">
                            {insight.title}
                          </h4>
                          <span className="text-xl font-bold" style={{ color: '#991B1B' }}>
                            {insight.metric}
                          </span>
                        </div>
                      </div>
                      <p className="text-sm text-[#1E3A8A]/70">
                        {insight.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* Future-Ready Note */}
        <section className="mb-12">
          <Card className="border-[#E5E7EB] bg-[#1E3A8A]/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Globe className="w-5 h-5 text-[#1E3A8A]" />
                <h3 className="font-heading font-bold text-[#1E3A8A]">Future-Ready Architecture</h3>
              </div>
              <p className="text-sm text-[#1E3A8A]/70 mb-4">
                This dashboard is structured to support organization-level aggregation, institutional 
                benchmarking, real-time updates, and government/enterprise deployments.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="flex items-center gap-2 text-sm text-[#1E3A8A]/60">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Organization Dashboards</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#1E3A8A]/60">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Industry Benchmarking</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#1E3A8A]/60">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Real-time Analytics</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* CTA */}
        <section className="text-center">
          {!hasCompletedAssessment ? (
            <div className="bg-[#991B1B] rounded-2xl p-8 text-white">
              <h2 className="font-heading text-2xl font-bold mb-4">
                Add Your Data to the Benchmark
              </h2>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                Complete the Workforce Intelligence Assessment to see how you compare to global benchmarks.
              </p>
              <Link to="/observatories/global/survey">
                <Button size="lg" className="bg-white text-[#991B1B] hover:bg-white/90">
                  Take Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="bg-[#1E3A8A] rounded-2xl p-8 text-white">
              <h2 className="font-heading text-2xl font-bold mb-4">
                View Your Complete Profile
              </h2>
              <p className="text-white/80 mb-6 max-w-lg mx-auto">
                See your personalized opportunities, tool recommendations, and development pathway.
              </p>
              <Link to="/juswork/profile">
                <Button size="lg" className="bg-white text-[#1E3A8A] hover:bg-white/90">
                  View Profile
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
